import React, { useState } from "react";
import { Spinner, Alert, Button } from "react-bootstrap";
import "./SalesForm.css";
import axios from "axios";
import BASE_URL, { BACKEND_URL } from "../config";
import OrderTracking from './OrderTracking.jsx';

const mapOrderAttachments = (order) => {
  const rawAttachments = Array.isArray(order.attachments?.data)
    ? order.attachments.data
    : Array.isArray(order.attachments) ? order.attachments : [];

  const pod_photos = rawAttachments.filter(a => a.type?.toLowerCase() === "pod_photo");
  const voice_notes = rawAttachments.filter(a => a.type?.toLowerCase() === "voice_note");
  const order_docs = rawAttachments.filter(a => a.type?.toLowerCase() === "order_document" || a.type?.toLowerCase() === "other");

  return {
    ...order,
    // Ensure driver_id is available on the order object so UI can read it
    driver_id: order.driver_id || order.driver?.id || order.driver?.driver_id || null,
    attachments: {
      pod_photos: pod_photos.map(a => ({ ...a, url: a.path ? `${BACKEND_URL}/${a.path}` : null })),
      voice_notes: voice_notes.map(a => ({ ...a, url: a.path ? `${BACKEND_URL}/${a.path}` : null })),
      order_docs: order_docs.map(a => ({ ...a, url: a.path ? `${BACKEND_URL}/${a.path}` : null }))
    }
  };
};

const handleOpenAttachment = (file) => {
  if (!file || !file.url) {
    alert("Attachment URL is missing.");
    return;
  }
  window.open(file.url, "_blank");
};

// Helper function to format ISO date string to readable format
const formatDateTime = (isoString) => {
  if (!isoString) return "—";
  try {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return isoString; // Return original if parsing fails
  }
};

const SalesFormOrderTrack = () => {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Track modal state
  const [trackModalOrder, setTrackModalOrder] = useState(null);
  const [showOrderTrackingModal, setShowOrderTrackingModal] = useState(false);

  const searchInvoice = async () => {
    if (invoiceNumber.trim() === "") {
      alert("Please enter a PO Number.");
      return;
    }

    setLoading(true);
    setError("");
    setOrderData(null);

    try {
      // Use embed to include related models and attachments
      const url = `${BASE_URL}/orders?po_number=${encodeURIComponent(invoiceNumber)}&embed=customer,vendor,driver,showroom,warehouse,attachments`;
      const response = await axios.get(url);
      
      // The API may return paginated data: response.data.data.data or response.data.data
      const orders = response?.data?.data?.data || response?.data?.data || [];

      console.log("✅ Orders fetched:", orders);

      if (orders.length > 0) {
        // Map attachments before setting state
        const processedOrder = mapOrderAttachments(orders[0]);
        console.log("✅ Processed order:", processedOrder);
        setOrderData(processedOrder); // Use the first matching order
      } else {
        setError("No order found for the given number.");
      }
    } catch (err) {
      console.error("Order search failed:", err.response?.status, err.response?.data || err.message);
      setError("No order found for the given number.");
    } finally {
      setLoading(false);
    }
  };

  // Track modal functions
  const handleTrackDriver = (order) => {
    // Compute driver id from the order record (order table) or nested driver relation
    const computedDriverId = order.driver_id || order.driver?.id || order.driver?.driver_id || null;

    console.log("✅ Order data passed to tracking:", {
      id: order.id,
      driver_id: computedDriverId,
      driver_name: order?.driver?.name,
      vendor: order?.vendor?.name,
      pickup: order.pickup_address,
      delivery: order.delivery_address,
      showroom: order?.showroom?.name,
      remarks: order.notes
    });

    // Attach computed driver_id to the modal order so child components always receive it
    setTrackModalOrder({ ...order, driver_id: computedDriverId });
    setShowOrderTrackingModal(true);
  };

  const handleCloseTrackModal = () => {
    setShowOrderTrackingModal(false);
    setTrackModalOrder(null);
  };

  return (
    <div className="border p-2 rounded" style={{ background: '#fff' }}>
      <style>{`
        .sales-form-order-track-container {
          max-width: 900px;
          margin: 0 auto;
          padding: 20px;
          background-color: #ffffff;
          border-radius: 8px;
        }
        .sales-form-order-track-container .search-area {
          display: flex;
          margin-bottom: 25px;
          padding: 15px;
          background-color: #e0f7fa;
          border-radius: 6px;
          align-items: center;
        }
        .sales-form-order-track-container .search-area input[type="text"] {
          padding: 10px;
          flex-grow: 1;
          border: 1px solid #00bcd4;
          border-radius: 4px 0 0 4px;
          font-size: 16px;
        }
        .sales-form-order-track-container .search-area button {
          padding: 10px 20px;
          background-color: #00bcd4;
          color: white;
          border: none;
          border-radius: 0 4px 4px 0;
          cursor: pointer;
          font-size: 16px;
          transition: background-color 0.3s;
        }
        .sales-form-order-track-container .search-area button:hover {
          background-color: #0097a7;
        }
        .sales-form-order-track-container .header-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 25px;
          padding-bottom: 15px;
          border-bottom: 2px solid #eee;
        }
        .sales-form-order-track-container .status-badge {
          font-size: 18px;
          font-weight: bold;
          padding: 8px 15px;
          border-radius: 20px;
          background-color: #ff9800;
          color: white;
        }
        .sales-form-order-track-container .tracking-link {
          text-decoration: none;
          background-color: #4CAF50;
          color: white;
          padding: 10px 20px;
          border-radius: 4px;
          font-weight: bold;
          transition: background-color 0.3s;
          display: inline-block;
          cursor: pointer;
        }
        .sales-form-order-track-container .tracking-link:hover {
          background-color: #45a049;
        }
        .sales-form-order-track-container h1 {
          color: #333;
          margin-bottom: 20px;
        }
        .sales-form-order-track-container h2 {
          color: #333;
          border-bottom: 2px solid #00bcd4;
          padding-bottom: 5px;
          margin-top: 30px;
          font-size: 20px;
        }
        .sales-form-order-track-container .detail-group {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          padding: 10px 0;
        }
        .sales-form-order-track-container .detail-item {
          padding: 10px;
          background-color: #f9f9f9;
          border-left: 4px solid #00bcd4;
          border-radius: 4px;
        }
        .sales-form-order-track-container .detail-item strong {
          display: block;
          color: #555;
          font-size: 14px;
          margin-bottom: 3px;
        }
        .sales-form-order-track-container .detail-item span {
          font-size: 16px;
          color: #333;
          font-weight: 600;
        }
        .sales-form-order-track-container .full-width {
          grid-column: span 2;
        }
        .sales-form-order-track-container .remarks {
          padding: 10px;
          background-color: #fff3e0;
          border-left: 4px solid #ff9800;
          border-radius: 4px;
          margin-top: 15px;
        }
      `}</style>

      <div className="sales-form-order-track-container">
        <h1>📦 Sales Order Tracking</h1>

        {/* Search Section */}
        <div className="search-area">
          <input
            type="text"
            placeholder="Enter SO/PO Number..."
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
          />
          <button onClick={searchInvoice}>Search</button>
        </div>

        {loading && (
          <div className="text-center">
            <Spinner animation="border" variant="info" />
          </div>
        )}

        {error && (
          <Alert variant="danger" className="text-center">
            {error}
          </Alert>
        )}

        {orderData && (
          <>
            <div className="header-info">
              <div className="status-badge">
                Status: {orderData.status || "Pending"}
              </div>
              <div className="tracking-link" onClick={() => handleTrackDriver(orderData)}>
                📍 Share Link for Live Tracking
              </div>
            </div>

            <h2>🧾 Invoice & Order Details</h2>
            <div className="detail-group">
              <div className="detail-item">
                <strong>UUID:</strong>
                <span style={{ fontSize: "20px" }}>
                  {orderData.uuid || "—"}
                </span>
              </div>
              <div className="detail-item">
                <strong>Order NO:</strong>
                <span>#{orderData.po_number || "—"}</span>
              </div>
              <div className="detail-item">
                <strong>Type:</strong>
                <span>{orderData.type || "Sales Order"}</span>
              </div>
              <div className="detail-item">
                <strong>Created:</strong>
                <span>{formatDateTime(orderData.created_at)}</span>
              </div>
              <div className="detail-item">
                <strong>Updated:</strong>
                <span>{formatDateTime(orderData.updated_at)}</span>
              </div>
              <div className="detail-item">
                <strong>Attachments:</strong>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {[
                    ...(orderData.attachments?.pod_photos || []),
                    ...(orderData.attachments?.voice_notes || []),
                    ...(orderData.attachments?.order_docs || [])
                  ].length > 0 ? (
                    [
                      ...(orderData.attachments.pod_photos || []),
                      ...(orderData.attachments.voice_notes || []),
                      ...(orderData.attachments.order_docs || [])
                    ].map((file, idx) => (
                      <Button key={idx} variant="outline-primary" size="sm" onClick={() => handleOpenAttachment(file)}>
                        <i className="fa fa-paperclip me-1"></i> {file.type?.replace(/_/g, ' ') || `File ${idx + 1}`}
                      </Button>
                    ))
                  ) : (<span>—</span>)}
                </div>
              </div>
            </div>

            <h2>📝 Remarks</h2>
            <div className="remarks">
              <strong>Remarks:</strong>
              <span>{orderData.notes || "—"}</span>
            </div>
          </>
        )}
      </div>

      {/* Track Modal */}
      <OrderTracking
        show={showOrderTrackingModal}
        handleClose={handleCloseTrackModal}
        driverId={trackModalOrder?.driver_id || trackModalOrder?.driver?.id}
        order={trackModalOrder}
      />
    </div>
  );
};

export default SalesFormOrderTrack;